// src/SuperHome.js
import React, { useEffect, useState } from 'react';
import API from './api';
import { Link } from 'react-router-dom';
import { Building2, Users, AlertTriangle, Activity, FileText } from 'lucide-react';

const SuperHome = () => {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'notifications'
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // { organizations, users, students, visits, notifications, org_list }
  const [error, setError] = useState('');

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await API.get('/super/dashboard');
      setData(res.data || null);
    } catch (err) {
      console.error('Super dashboard load error', err);
      setError('Failed to load super admin dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const org = data?.organizations || {};
  const users = data?.users || {};
  const students = data?.students || {};
  const visits = data?.visits || {};
  const notifications = data?.notifications || [];
  const orgList = data?.org_list || [];

  const totalAdmins = Number(users.admin_count ?? 0);
  const totalTeachers = Number(users.teacher_count ?? 0);
  const totalProviders = totalAdmins + totalTeachers;

  return (
    <div className="p-0">
      {/* Tabs */}
      <div className="flex gap-2 border-b mb-3 px-1 pt-3">
        <button
          className={
            'px-2 py-1.5 text-sm rounded-t ' +
            (activeTab === 'overview'
              ? 'bg-white border border-b-white border-gray-300 font-semibold'
              : 'bg-gray-100 text-gray-700 border border-transparent hover:bg-gray-200')
          }
          onClick={() => setActiveTab('overview')}
        >
          SaaS overview
        </button>

        <button
          className={
            'px-2 py-1.5 text-sm rounded-t ' +
            (activeTab === 'notifications'
              ? 'bg-white border border-b-white border-gray-300 font-semibold'
              : 'bg-gray-100 text-gray-700 border border-transparent hover:bg-gray-200')
          }
          onClick={() => setActiveTab('notifications')}
        >
          Billing & plan alerts
        </button>
      </div>

      {/* Content */}
      <div className="px-0 pb-4">
        {loading && (
          <div className="p-4 text-sm text-gray-500">Loading…</div>
        )}
        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
            {error}
          </div>
        )}

        {!loading && !error && activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Top cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* Organizations card */}
              <div className="bg-white rounded-xl shadow-sm p-3 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-100 text-slate-700">
                  <Building2 size={20} />
                </div>
                <div>
                  <div className="text-xs text-gray-500">Organizations</div>
                  <div className="text-lg font-semibold">
                    {org.total_orgs ?? 0}
                  </div>
                  <div className="text-[11px] text-gray-400">
                    {org.active_orgs ?? 0} active · {org.trial_orgs ?? 0} trial
                  </div>
                </div>
              </div>

              {/* Providers & admins card */}
              <div className="bg-white rounded-xl shadow-sm p-3 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-100 text-slate-700">
                  <Users size={20} />
                </div>
                <div>
                  <div className="text-xs text-gray-500">Providers & admins</div>
                  <div className="text-lg font-semibold">
                    {totalAdmins} / {totalTeachers}
                  </div>
                  <div className="text-[11px] text-gray-400">
                    admins / teachers · total {totalProviders}
                  </div>
                </div>
              </div>

              {/* Students card */}
              <div className="bg-white rounded-xl shadow-sm p-3 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-100 text-slate-700">
                  <Users size={20} />
                </div>
                <div>
                  <div className="text-xs text-gray-500">Students</div>
                  <div className="text-lg font-semibold">
                    {students.total_students ?? 0}
                  </div>
                  <div className="text-[11px] text-gray-400">
                    {students.active_students ?? 0} active
                  </div>
                </div>
              </div>

              {/* Visits card */}
              <div className="bg-white rounded-xl shadow-sm p-3 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-100 text-slate-700">
                  <Activity size={20} />
                </div>
                <div>
                  <div className="text-xs text-gray-500">Total visits</div>
                  <div className="text-lg font-semibold">
                    {visits.total_visits ?? 0}
                  </div>
                  <div className="text-[11px] text-gray-400">
                    Across all organizations
                  </div>
                </div>
              </div>
            </div>

            {/* Past due alert */}
            {org.past_due_orgs > 0 && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={16} />
                  <div>
                    <span className="font-semibold">
                      {org.past_due_orgs} organization
                      {org.past_due_orgs > 1 ? 's are' : ' is'} past due.
                    </span>{' '}
                    <span>
                      Check billing status and consider sending reminders or limiting features.
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Organizations detail table */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold">
                  Organizations detail
                </h3>
                <p className="text-[11px] text-gray-400">
                  Per-tenant breakdown: name, plan, status, key counts & quick access to invoices.
                </p>
              </div>

              {!orgList.length && (
                <p className="text-xs text-gray-500">
                  No organizations found yet.
                </p>
              )}

              {orgList.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-1 px-2">Organization</th>
                        <th className="text-left py-1 px-2">Slug</th>
                        <th className="text-left py-1 px-2">Plan</th>
                        <th className="text-left py-1 px-2">Status</th>
                        <th className="text-left py-1 px-2">Timezone</th>
                        <th className="text-right py-1 px-2">Admins</th>
                        <th className="text-right py-1 px-2">Teachers</th>
                        <th className="text-right py-1 px-2">Students</th>
                        <th className="text-right py-1 px-2">Visits</th>
                        <th className="text-right py-1 px-2">Billing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgList.map((o) => (
                        <tr key={o.id} className="border-b last:border-0">
                          <td className="py-1 px-2 text-gray-800">
                            {o.name}
                            {!o.is_active && (
                              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px]">
                                inactive
                              </span>
                            )}
                          </td>
                          <td className="py-1 px-2 text-gray-500">
                            {o.slug}
                          </td>
                          <td className="py-1 px-2 text-gray-500">
                            {o.billing_plan}
                          </td>
                          <td className="py-1 px-2">
                            <span
                              className={
                                'px-2 py-0.5 rounded-full text-[10px] ' +
                                (o.subscription_status === 'past_due'
                                  ? 'bg-red-100 text-red-700'
                                  : o.subscription_status === 'trial'
                                  ? 'bg-amber-100 text-amber-700'
                                  : o.subscription_status === 'active'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-gray-100 text-gray-600')
                              }
                            >
                              {o.subscription_status}
                            </span>
                          </td>
                          <td className="py-1 px-2 text-gray-500">
                            {o.timezone}
                          </td>
                          <td className="py-1 px-2 text-right text-gray-800">
                            {o.admin_count}
                          </td>
                          <td className="py-1 px-2 text-right text-gray-800">
                            {o.teacher_count}
                          </td>
                          <td className="py-1 px-2 text-right text-gray-800">
                            {o.student_count}
                          </td>
                          <td className="py-1 px-2 text-right text-gray-800">
                            {o.total_visits}
                          </td>
                          <td className="py-1 px-2 text-right">
                            <Link
                              to={`/super/billing?org_id=${o.id}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200"
                            >
                              <FileText size={12} />
                              <span>Invoices</span>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && !error && activeTab === 'notifications' && (
          <div className="bg-white rounded-xl shadow-sm p-3">
            <h3 className="text-sm font-semibold mb-2">
              Latest organization notifications
            </h3>
            {!notifications.length && (
              <p className="text-xs text-gray-500">
                No notifications yet.
              </p>
            )}
            {notifications.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1 px-2">Org</th>
                      <th className="text-left py-1 px-2">Type</th>
                      <th className="text-left py-1 px-2">Severity</th>
                      <th className="text-left py-1 px-2">Title</th>
                      <th className="text-left py-1 px-2">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notifications.map((n) => (
                      <tr key={n.id} className="border-b last:border-0">
                        <td className="py-1 px-2 text-gray-800">
                          {n.organization_name}
                        </td>
                        <td className="py-1 px-2 text-gray-500">{n.type}</td>
                        <td className="py-1 px-2">
                          <span
                            className={
                              'px-2 py-0.5 rounded-full text-[10px] ' +
                              (n.severity === 'critical'
                                ? 'bg-red-100 text-red-700'
                                : n.severity === 'warning'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-slate-100 text-slate-700')
                            }
                          >
                            {n.severity}
                          </span>
                        </td>
                        <td className="py-1 px-2 text-gray-700">
                          {n.title}
                        </td>
                        <td className="py-1 px-2 text-gray-400">
                          {n.created_at}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperHome;



